import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { diffTemplates } from "./diff";
import {
  useAdminProject,
  usePublishQuestionnaire,
  useValidateQuestionnaire,
  type QuestionnaireJson,
  type ValidateOut,
} from "@/api/hooks/admin";
import { useQuestionnaire } from "@/api/hooks/questionnaire";
import { errorMessage } from "@/api/errors";
import { ErrorNotice } from "@/components/ErrorNotice";
import { Field, FormError } from "@/components/Field";
import { PageHeader } from "@/components/PageHeader";
import { PageLoading } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import type { Template } from "@/features/questionnaire/types";
import { usePageCrumbs } from "@/app/layouts/shellContext";

function newKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** "/sections/0/questions/0: message" → ["/sections/0/questions/0", "message"]. */
function splitError(e: string): [string | null, string] {
  const i = e.indexOf(": ");
  if (i > 0 && e.startsWith("/")) return [e.slice(0, i), e.slice(i + 2)];
  return [null, e];
}

function DiffList({ title, ids, detail }: { title: string; ids: string[]; detail?: (id: string) => string | null }) {
  if (ids.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <h4>{title}</h4>
      <ul className="flex flex-col gap-1">
        {ids.map((id) => (
          <li key={id} className="flex flex-wrap items-baseline gap-2 text-sm">
            <code className="rounded-sm bg-surface-muted px-1.5 py-0.5">{id}</code>
            {detail?.(id) ? <span className="text-fg-muted">{detail(id)}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PublishQuestionnairePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const project = useAdminProject(id);
  usePageCrumbs([
    { label: t("admin.nav.orgs"), to: "/admin" },
    { label: project.data?.name ?? "", to: `/admin/projects/${id}` },
    { label: t("admin.publish.title") },
  ]);
  const latest = useQuestionnaire(project.data?.organisation_slug ?? "", project.data?.slug ?? "", { enabled: Boolean(project.data) });
  const validate = useValidateQuestionnaire(id);
  const publish = usePublishQuestionnaire(id);
  const [text, setText] = useState("");
  const [notes, setNotes] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [validated, setValidated] = useState<{ text: string; body: QuestionnaireJson; result: ValidateOut; key: string } | null>(null);
  const [stale, setStale] = useState(false);

  // Any edit after validating invalidates the result, even if the text ends up identical.
  const current = validated && validated.text === text && !stale ? validated : null;
  const edit = (value: string) => {
    setText(value);
    if (validated) setStale(true);
  };
  const clean = Boolean(current && current.result.errors.length === 0);

  const preview = useMemo(() => {
    if (!current || !clean) return null;
    try {
      return diffTemplates(latest.data?.template?.body ?? null, current.body as Template);
    } catch {
      return null;
    }
  }, [current, clean, latest.data]);

  if (project.isPending) return <PageLoading />;
  if (project.isError) return <ErrorNotice error={project.error} />;

  const onValidate = () => {
    setJsonError(null);
    let body: QuestionnaireJson;
    try {
      const parsed: unknown = JSON.parse(text);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        throw new Error("the top level must be a JSON object");
      }
      body = parsed as QuestionnaireJson;
    } catch (e) {
      setJsonError(t("admin.publish.invalidJson", { message: e instanceof Error ? e.message : String(e) }));
      setValidated(null);
      return;
    }
    const snapshot = text;
    validate.mutate(body, {
      onSuccess: (result) => {
        setValidated({ text: snapshot, body, result, key: newKey() });
        setStale(false);
      },
    });
  };

  const onPublish = () => {
    if (!current || !clean) return;
    publish.mutate(
      { body: current.body, notes, idempotencyKey: current.key },
      {
        onSuccess: (out) => {
          toast.success(t("admin.publish.published", { version: out.version }));
          navigate(`/admin/projects/${id}`);
        },
      },
    );
  };

  const onFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => edit(typeof reader.result === "string" ? reader.result : "");
    reader.readAsText(file);
  };

  const changedFields = (qid: string) => {
    const c = preview?.changed.find((x) => x.id === qid);
    return c ? c.fields.map((f) => t(`admin.publish.field.${f}`)).join(", ") : null;
  };

  const diff = current?.result.diff;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("admin.publish.title")} description={`${project.data.name} · ${t("admin.publish.intro")}`} />
      <div className="grid gap-6 lg:grid-cols-split">
        <Card>
          <CardContent className="flex flex-col gap-4 pt-6">
            <Field label={t("admin.publish.upload")}>
              {(p) => (
                <input
                  {...p}
                  type="file"
                  accept="application/json,.json"
                  className="block w-full text-sm file:mr-4 file:min-h-11 file:cursor-pointer file:rounded-full file:border-2 file:border-secondary file:bg-surface file:px-5 file:font-semibold file:text-heading hover:file:bg-secondary-subtle"
                  onChange={(e) => onFile(e.target.files?.[0])}
                />
              )}
            </Field>
            <Field label={t("admin.publish.json")} error={jsonError}>
              {(p) => (
                <Textarea
                  {...p}
                  rows={18}
                  spellCheck={false}
                  className="font-mono text-sm"
                  value={text}
                  onChange={(e) => edit(e.target.value)}
                />
              )}
            </Field>
            <Field label={t("admin.publish.notes")} hint={t("admin.publish.notesHint")}>
              {(p) => <Textarea {...p} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />}
            </Field>
            <FormError message={validate.isError ? errorMessage(validate.error, t("errors.generic")) : null} />
            <FormError message={publish.isError ? errorMessage(publish.error, t("errors.generic")) : null} />
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="secondary" onClick={onValidate} disabled={!text.trim() || validate.isPending}>
                {validate.isPending ? t("admin.publish.validating") : t("admin.publish.validate")}
              </Button>
              <Button onClick={onPublish} disabled={!clean || publish.isPending} aria-describedby="publish-hint">
                {publish.isPending ? t("admin.publish.publishing") : t("admin.publish.publish")}
              </Button>
              <span id="publish-hint" className="text-sm text-fg-muted">
                {validated && stale ? t("admin.publish.staleHint") : !clean ? t("admin.publish.mustValidate") : null}
              </span>
            </div>
          </CardContent>
        </Card>

        <div aria-live="polite" className="flex flex-col gap-4">
          {current && current.result.errors.length > 0 ? (
            <Card className="border-danger-muted">
              <CardContent className="flex flex-col gap-3 pt-6">
                <h3 className="flex items-center gap-2 text-lg">
                  <AlertTriangle aria-hidden="true" className="h-5 w-5 text-danger" />
                  {t("admin.publish.errorsTitle")}
                </h3>
                <ul className="flex flex-col gap-2">
                  {current.result.errors.map((e) => {
                    const [path, message] = splitError(e);
                    return (
                      <li key={e} className="flex flex-col gap-0.5 text-sm">
                        {path ? <code className="font-mono text-fg-muted">{path}</code> : null}
                        <span>{message}</span>
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          ) : null}
          {clean && diff ? (
            <Card>
              <CardContent className="flex flex-col gap-4 pt-6">
                <p className="flex items-center gap-2 font-semibold text-heading">
                  <CheckCircle2 aria-hidden="true" className="h-5 w-5" />
                  {t("admin.publish.valid")}
                </p>
                <p className="font-heading text-lg font-semibold text-heading">
                  {t("admin.publish.diffSummary", { count: diff.added.length, removed: diff.removed.length, changed: diff.changed.length })}
                </p>
                <DiffList title={t("admin.publish.added")} ids={diff.added} />
                <DiffList title={t("admin.publish.removed")} ids={diff.removed} />
                <DiffList title={t("admin.publish.changed")} ids={diff.changed} detail={changedFields} />
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
